import { REHYDRATE } from 'redux-persist/constants';
import { camelCase } from 'lodash';

import createReducer from '../../helpers/CreateReducer';
import storage from '../../helpers/StorageHelper';
import { ApiConstants } from '../../constants/actions';
import { Roles } from '../../constants/roles';

interface LoginState {
  isAuthenticated: boolean;
  isFetching: boolean;
  accessToken: string;
  tokenType: string;
  expiresIn: string;
  refreshToken: string;
  refreshingToken: boolean;
  validTo: string | null;
  roles: string[];
  hasOrders: boolean;
  isUserImpersonate: boolean;
  isHasOrdersFetched: boolean;
  isUserUnverified: boolean;
  username: string;
  rehydrated?: boolean;
}

const getRememberMeAuthenticationData = (): Partial<LoginState> =>
  storage.get('rememberMe') || {};

export const getInitialState = (allowRememberMe = true): LoginState => ({
  isAuthenticated: false,
  isFetching: false,
  accessToken: '',
  tokenType: '',
  expiresIn: '',
  refreshToken: '',
  refreshingToken: false,
  validTo: null,
  roles: [],
  hasOrders: false,
  isUserImpersonate: false,
  isHasOrdersFetched: false,
  isUserUnverified: false,
  username: '',
  ...(allowRememberMe ? getRememberMeAuthenticationData() : {})
});

export default createReducer(getInitialState, {
  [`${ApiConstants.LOGIN_ACTION}_REQUEST`]: (state): Partial<LoginState> => ({
    isFetching: true
  }),
  [`${ApiConstants.LOGIN_ACTION}_SUCCESS`]: (
    state,
    { payload }
  ): Partial<LoginState> => ({
    accessToken: payload!.accessToken,
    tokenType: payload!.tokenType,
    expiresIn: payload!.expiresIn,
    refreshToken: payload!.refreshToken,
    isAuthenticated: true,
    isFetching: false,
    refreshingToken: false,
    isUserImpersonate: payload!.isUserImpersonate
      ? payload!.isUserImpersonate
      : false,
    validTo: payload!.validTo,
    roles: (payload!.roles || []).map(camelCase),
    isUserUnverified: false
  }),
  [`${ApiConstants.LOGIN_ACTION}_FAILURE`]: (state): Partial<LoginState> => ({
    isFetching: false
  }),
  [`${ApiConstants.LOGIN_ACTION}_UNVERIFIED`]: (
    state,
    { payload }
  ): Partial<LoginState> => ({
    isUserUnverified: true,
    username: payload!.username || '',
    isFetching: false
  }),

  [`${ApiConstants.LOGOUT_ACTION}_REQUEST`]: (state): Partial<LoginState> => ({
    isFetching: true
  }),
  [`${ApiConstants.LOGOUT_ACTION}_SUCCESS`]: (): LoginState =>
    getInitialState(false),
  [`${ApiConstants.LOGOUT_ACTION}_FAILURE`]: (state): Partial<LoginState> => ({
    isFetching: false,
    refreshingToken: false
  }),

  [`${ApiConstants.REFRESH_TOKEN}_REQUEST`]: (state): Partial<LoginState> => ({
    refreshingToken: true
  }),
  [`${ApiConstants.REFRESH_TOKEN}_SUCCESS`]: (
    state,
    { payload }
  ): Partial<LoginState> => ({
    ...state,
    isAuthenticated: true,
    validTo: payload!.validTo,
    accessToken: payload!.accessToken,
    refreshToken: payload!.refreshToken,
    refreshingToken: false
  }),

  [`${ApiConstants.ADD_SIGNATORY_ROLE_TO_USER}_SUCCESS`]: (
    state
  ): Partial<LoginState> => ({
    ...state,
    roles: state.roles.concat(Roles.signatory)
  }),

  [`${ApiConstants.GET_HAS_ORDERS}_LOADED`]: (
    state,
    { payload }
  ): Partial<LoginState> => ({
    ...state,
    hasOrders: payload!.hasOrders || false,
    isHasOrdersFetched: true
  }),

  [`${REHYDRATE}`](state, action): Partial<LoginState> {
    return {
      ...state,
      ...(action.payload?.login || {}),
      isFetching: false,
      isUserUnverified: false,
      username: '',
      rehydrated: true
    };
  }
});
